(() => {
  const startAtmosphere = (root) => {
    const canvas = root.querySelector('[data-pa-advent-atmosphere]');
    const context = canvas && canvas.getContext('2d', { alpha: true });
    if (!context) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let width = 0;
    let height = 0;
    let flakes = [];
    let glints = [];
    let edgeGlints = [];
    let previous = 0;
    let frame = 0;
    let observer;
    let intersectionObserver;
    let disposed = false;
    let visible = true;
    let dialogOpen = false;
    let viewportDirty = true;
    let offset = 0;
    let renderHeight = 0;
    let activeEdgeGlints = [];
    let ready = false;
    let setupTimer;
    let idleTask;
    let quality = 1;
    let slowFrames = 0;
    let drawAverage = 0;
    const sprites = [];
    const pulseTable = Array.from({ length: 256 }, (_, i) => Math.pow(Math.max(0, Math.sin(i / 256 * Math.PI * 2)), 4));
    const mobile = () => width < 750;
    const canAnimate = () => !disposed && visible && !document.hidden && !dialogOpen;
    const schedule = () => {
      if (!frame && ready && canAnimate()) frame = window.requestAnimationFrame(draw);
    };
    const cleanup = () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(setupTimer);
      if (idleTask && window.cancelIdleCallback) window.cancelIdleCallback(idleTask);
      if (observer) observer.disconnect();
      if (intersectionObserver) intersectionObserver.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', restart);
      reduceMotion.removeEventListener('change', restart);
      document.removeEventListener('shopify:section:unload', onUnload);
      root.dataset.paAtmosphereState = 'disposed';
      canvas.width = canvas.height = 0;
      sprites.length = 0;
      flakes = glints = edgeGlints = activeEdgeGlints = [];
    };
    const restart = () => {
      window.cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      viewportDirty = true;
      root.dataset.paAtmosphereState = canAnimate() ? (reduceMotion.matches ? 'static' : 'running') : 'paused';
      schedule();
    };
    const onScroll = () => {
      viewportDirty = true;
      if (ready && !visible) syncViewport();
      schedule();
    };
    const onUnload = (event) => {
      if (event.target === root || event.target.contains(root)) cleanup();
    };

    const makeFlake = (initial = false) => ({
      x: Math.random() * width,
      y: initial ? Math.random() * height : -8 - Math.random() * 35,
      radius: .65 + Math.random() * 2.05,
      speed: 27 + Math.random() * 56,
      drift: -9 + Math.random() * 18,
      opacity: .3 + Math.random() * .58
    });

    // Small light textures are painted once; no blur/shadow is calculated per particle.
    const makeSprites = () => {
      ['#ffe4aa', '#fff9ef', '#fffdf7'].forEach((color, index) => {
        const sprite = document.createElement('canvas');
        sprite.width = sprite.height = 24;
        const paint = sprite.getContext('2d');
        const glow = paint.createRadialGradient(12, 12, 0, 12, 12, 12);
        glow.addColorStop(0, color);
        glow.addColorStop(.12, color);
        glow.addColorStop(.3, index === 2 ? '#fffdf780' : '#f9cc7855');
        glow.addColorStop(1, '#ffffff00');
        paint.fillStyle = glow;
        paint.fillRect(0, 0, 24, 24);
        sprites.push(sprite);
      });
    };
    const refreshOpenGlints = () => {
      const open = new Set(Array.from(root.querySelectorAll('.pa-advent__door.is-open')));
      activeEdgeGlints = edgeGlints.filter(point => !point.onlyWhenOpen || open.has(point.onlyWhenOpen));
      root.dataset.paAtmospherePoints = String(glints.length + activeEdgeGlints.length + flakes.length);
    };

    const buildEdgeGlints = () => {
      const rootBox = root.getBoundingClientRect();
      edgeGlints = [];
      const addPoint = (x, y, onlyWhenOpen = null) => edgeGlints.push({
        x,
        y,
        onlyWhenOpen,
        radius: .45 + Math.random() * 1.25,
        phase: Math.random() * Math.PI * 2,
        speed: .8 + Math.random() * 3.1
      });
      const addOutline = (element, spacing, onlyWhenOpen = null) => {
        const box = element.getBoundingClientRect();
        const w = box.width;
        const h = box.height;
        const style = getComputedStyle(element);
        const radius = (value) => {
          const parts = value.split(' ');
          const size = (part, basis) => part.endsWith('%') ? parseFloat(part) * basis / 100 : parseFloat(part) || 0;
          return [Math.min(w / 2, size(parts[0], w)), Math.min(h / 2, size(parts[1] || parts[0], h))];
        };
        const corners = [
          [radius(style.borderTopLeftRadius), 0, 0, Math.PI],
          [radius(style.borderTopRightRadius), w, 0, Math.PI * 1.5],
          [radius(style.borderBottomRightRadius), w, h, 0],
          [radius(style.borderBottomLeftRadius), 0, h, Math.PI * .5]
        ];
        const path = [];
        corners.forEach(([r, x, y, angle]) => {
          const cx = x === 0 ? r[0] : w - r[0];
          const cy = y === 0 ? r[1] : h - r[1];
          for (let i = 0; i <= 16; i += 1) {
            const a = angle + i / 16 * Math.PI / 2;
            path.push([cx + r[0] * Math.cos(a), cy + r[1] * Math.sin(a)]);
          }
        });
        let carry = Math.random() * spacing;
        path.forEach((point, index) => {
          const next = path[(index + 1) % path.length];
          const dx = next[0] - point[0];
          const dy = next[1] - point[1];
          const length = Math.hypot(dx, dy);
          while (carry < length) {
            const t = length ? carry / length : 0;
            addPoint(box.left - rootBox.left + point[0] + dx * t, box.top - rootBox.top + point[1] + dy * t, onlyWhenOpen);
            carry += spacing * (.8 + Math.random() * .4);
          }
          carry -= length;
        });
      };
      root.querySelectorAll('.pa-advent__door').forEach((door) => {
        addOutline(door, mobile() ? 10 : 12);
        const bars = door.querySelector('.pa-advent__window-bars').getBoundingClientRect();
        for (let i = 1; i < 8; i += 1) {
          addPoint(bars.left - rootBox.left + bars.width / 2, bars.top - rootBox.top + bars.height * i / 8, door);
          addPoint(bars.left - rootBox.left + bars.width * i / 8, bars.top - rootBox.top + bars.height / 2, door);
        }
        const decoration = door.querySelector('.pa-advent__window-scene');
        const decoBox = decoration.getBoundingClientRect();
        const ornament = decoration.dataset && (mobile() ? decoration.dataset.mobileDecor : decoration.dataset.decor);
        if (ornament === 'wreath' || decoration.classList.contains('pa-advent__window-scene--wreath')) {
          for (let i = 0; i < 24; i += 1) {
            const angle = i / 24 * Math.PI * 2;
            addPoint(
              decoBox.left - rootBox.left + decoBox.width * (.5 + .5 * Math.cos(angle)),
              decoBox.top - rootBox.top + decoBox.height * (.5 + .5 * Math.sin(angle)),
              door
            );
          }
        } else {
          for (let i = 0; i < 25; i += 1) {
            const y = .12 + Math.random() * .74;
            const spread = .08 + y * .38;
            addPoint(
              decoBox.left - rootBox.left + decoBox.width * (.5 + (Math.random() * 2 - 1) * spread),
              decoBox.top - rootBox.top + decoBox.height * y,
              door
            );
          }
        }
      });
      root.querySelectorAll('.pa-advent__upper-window, .pa-advent__arch, .pa-advent__entrance, .pa-advent__wordmark').forEach((element) => addOutline(element, 9));
      root.querySelectorAll('.pa-advent__wreath').forEach((element) => addOutline(element, 5));
      root.querySelectorAll('.pa-advent__fir').forEach((element) => {
        const box = element.getBoundingClientRect();
        for (let i = 0; i < 45; i += 1) {
          const y = .12 + Math.random() * .73;
          addPoint(box.left - rootBox.left + box.width * (.5 + (Math.random() - .5) * y), box.top - rootBox.top + box.height * y);
        }
      });
      const mark = root.querySelector('.pa-advent__pediment-mark');
      if (mark) {
        const box = mark.getBoundingClientRect();
        for (let i = 0; i < 12; i += 1) addPoint(box.left - rootBox.left + box.width * (.3 + Math.random() * .4), box.top - rootBox.top + box.height * Math.random());
      }
      refreshOpenGlints();
    };

    const syncViewport = () => {
      viewportDirty = false;
      const bounds = root.getBoundingClientRect();
      const viewHeight = window.innerHeight || height;
      visible = bounds.bottom > 0 && bounds.top < viewHeight;
      if (!visible) return;
      const nextHeight = Math.min(height, viewHeight + 128);
      offset = Math.max(0, Math.min(height - nextHeight, -bounds.top - 64));
      canvas.style.top = offset + 'px';
      if (nextHeight === renderHeight) return;
      renderHeight = nextHeight;
      // Bound the visible surface even on Retina screens and very tall pages.
      const pixelBudget = mobile() ? 650000 : 1200000;
      const ratio = Math.min(window.devicePixelRatio || 1, mobile() ? 1 : 1.25, Math.sqrt(pixelBudget / (width * renderHeight)));
      canvas.width = Math.max(1, Math.round(width * ratio));
      canvas.height = Math.max(1, Math.round(renderHeight * ratio));
      canvas.style.height = renderHeight + 'px';
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      root.dataset.paAtmospherePixels = String(canvas.width * canvas.height);
    };

    const resize = () => {
      if (disposed || !ready) return;
      const bounds = root.getBoundingClientRect();
      const nextWidth = Math.max(1, Math.round(bounds.width));
      const nextHeight = Math.max(1, Math.round(bounds.height));
      if (nextWidth === width && nextHeight === height) { viewportDirty = true; restart(); return; }
      width = nextWidth;
      height = nextHeight;
      renderHeight = 0;
      viewportDirty = true;
      syncViewport();
      const area = width * height;
      flakes = Array.from({ length: Math.min(mobile() ? 190 : 300, Math.max(130, Math.round(area / 6500))) }, () => makeFlake(true));
      glints = Array.from({ length: Math.min(720, Math.max(350, Math.round(area / 2400))) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: .35 + Math.random() * 1.25,
        phase: Math.random() * Math.PI * 2,
        speed: .45 + Math.random() * 2.4,
        warmth: Math.random() > .38
      }));
      buildEdgeGlints();
      restart();
    };

    const draw = (now) => {
      frame = 0;
      if (!root.isConnected) { cleanup(); return; }
      if (!canAnimate()) return;
      if (viewportDirty) syncViewport();
      if (!visible) { root.dataset.paAtmosphereState = 'paused'; return; }
      if (!reduceMotion.matches && now - previous < (mobile() ? 1000 / 24 : 1000 / 30)) { schedule(); return; }
      const started = performance.now();
      const delta = previous ? Math.min((now - previous) / 1000, .08) : 0;
      previous = now;
      context.clearRect(0, 0, width, renderHeight);
      const seconds = now / 1000;
      const pulseFor = point => reduceMotion.matches ? .38 : pulseTable[Math.floor((seconds * point.speed + point.phase) * 256 / (Math.PI * 2)) & 255];
      const drawLight = (point, sprite, alpha, size) => {
        if (point.y < offset - 14 || point.y > offset + renderHeight + 14) return;
        context.globalAlpha = alpha;
        context.drawImage(sprites[sprite], point.x - size / 2, point.y - offset - size / 2, size, size);
      };

      for (let i = 0; i < Math.ceil(glints.length * quality); i += 1) {
        const point = glints[i];
        const pulse = pulseFor(point);
        drawLight(point, point.warmth ? 0 : 1, .12 + pulse * .88, point.radius * 5 + pulse * 5);
      }

      activeEdgeGlints.forEach((point, index) => {
        if (quality < .8 && index % 4 === 0) return;
        const pulse = pulseFor(point);
        drawLight(point, 0, .24 + pulse * .76, point.radius * 5 + pulse * 6);
      });

      flakes.forEach((flake, index) => {
        if (!reduceMotion.matches) {
          flake.y += flake.speed * delta;
          flake.x += flake.drift * delta;
          if (flake.y > height + 8 || flake.x < -15 || flake.x > width + 15) flakes[index] = makeFlake();
        }
        drawLight(flake, 2, flake.opacity, flake.radius * 4);
      });
      context.globalAlpha = 1;
      drawAverage = drawAverage ? drawAverage * .9 + (performance.now() - started) * .1 : performance.now() - started;
      if (drawAverage > 7) slowFrames += 1;
      else slowFrames = 0;
      if (slowFrames >= 12) { quality = Math.max(.55, quality - .15); slowFrames = 0; }
      root.dataset.paAtmosphereQuality = quality.toFixed(2);
      root.dataset.paAtmosphereDrawMs = drawAverage.toFixed(2);
      root.dataset.paAtmosphereFrames = String((Number(root.dataset.paAtmosphereFrames) || 0) + 1);
      if (!reduceMotion.matches) schedule();
    };

    const setup = () => {
      if (disposed || !root.isConnected) return;
      makeSprites();
      ready = true;
      resize();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!disposed) { buildEdgeGlints(); restart(); } });
      if ('ResizeObserver' in window) {
        observer = new ResizeObserver(resize);
        observer.observe(root);
      }
      else window.addEventListener('resize', resize, { passive: true });
      if ('IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(entries => {
          visible = entries[0].isIntersecting;
          restart();
        });
        intersectionObserver.observe(root);
      }
      restart();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', restart);
    reduceMotion.addEventListener('change', restart);
    document.addEventListener('shopify:section:unload', onUnload);
    if (window.requestIdleCallback) idleTask = window.requestIdleCallback(setup, { timeout: 600 });
    else setupTimer = window.setTimeout(setup, 32);
    return (options = {}) => {
      if (disposed) return;
      if (typeof options.dialogOpen === 'boolean') dialogOpen = options.dialogOpen;
      if (ready) refreshOpenGlints();
      restart();
    };
  };

  const init = (root) => {
    if (root.dataset.paAdventReady) return;
    const dialog = root.querySelector('[data-pa-advent-dialog]');
    const settings = JSON.parse(root.querySelector('script[data-pa-advent-settings]').textContent);
    const preview = root.dataset.preview !== 'false';
    const year = Number(root.dataset.year);
    const add = root.querySelector('[data-pa-advent-add]');
    const cartStatus = root.querySelector('[data-pa-advent-cart-status]');
    const discover = root.querySelector('a.pa-advent__cta');
    const discoverHref = discover.href;
    const discoverText = discover.querySelector('span').textContent;
    const campaign = window.PAAdventOffers;
    if (!campaign) return; // Fail closed if campaign asset could not load.
    const dateState = (day, now = new Date()) => campaign.dateState(day, year, preview, now);
    const dayCopy = campaign.copy[root.dataset.language] || campaign.copy.pl;
    root.dataset.paAdventReady = 'true';
    const refreshAtmosphere = startAtmosphere(root);
    const title = root.querySelector('[data-pa-advent-title]');
    const body = title.nextElementSibling;
    const dayLabel = root.querySelector('[data-pa-advent-day]');
    const close = root.querySelector('[data-pa-advent-close]');
    let lastDoor;
    let revealTimer;
    let adding = false;
    const doors = [...root.querySelectorAll('.pa-advent__door')];
    const refreshDates = () => {
      doors.forEach(door => {
        const state = dateState(Number(door.dataset.day));
        door.disabled = state !== 'current';
        door.classList.toggle('is-expired', state === 'expired');
        if (state === 'expired') door.classList.add('is-open');
        if (state === 'future') door.classList.remove('is-open');
        door.setAttribute('aria-description', state === 'expired' ? settings.expired : state === 'future' ? settings.future : '');
      });
      if (lastDoor && dateState(Number(lastDoor.dataset.day)) !== 'current') {
        add.hidden = true;
        discover.hidden = true;
        body.textContent = settings.expired;
      }
      if (refreshAtmosphere) refreshAtmosphere();
    };
    refreshDates();
    // Check boundaries again after sleep / tab suspension, not just initial load.
    const dateTimer = window.setInterval(refreshDates, 30000);
    const refreshOnVisibility = () => { if (!document.hidden) refreshDates(); };
    document.addEventListener('visibilitychange', refreshOnVisibility);

    doors.forEach((door) => {
      door.addEventListener('click', () => {
        const day = Number(door.dataset.day);
        if (adding || dateState(day) !== 'current') { refreshDates(); return; }
        lastDoor = door;
        door.classList.add('is-open');
        if (refreshAtmosphere) refreshAtmosphere();
        dayLabel.textContent = day;
        const offerCopy = dayCopy[day - 1];
        title.textContent = offerCopy[0];
        body.textContent = offerCopy[1];
        const reward = settings.days[String(day)];
        add.hidden = true; // Campaign checkout integration is not activated yet.
        add.disabled = false;
        cartStatus.textContent = '';
        discover.hidden = false;
        discover.href = discoverHref;
        discover.querySelector('span').textContent = discoverText;
        const ornament = door.querySelector('[data-decor]');
        const modalOrnament = dialog.querySelector('[data-decor]');
        if (ornament && modalOrnament) {
          const decor = window.matchMedia('(max-width: 750px)').matches ? ornament.dataset.mobileDecor : ornament.dataset.decor;
          modalOrnament.dataset.decor = modalOrnament.dataset.mobileDecor = decor;
        }
        window.clearTimeout(revealTimer);
        revealTimer = window.setTimeout(() => {
          if (!root.isConnected) return;
          if (dateState(day) !== 'current') { refreshDates(); return; }
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
          door.setAttribute('aria-expanded', 'true');
          if (refreshAtmosphere) refreshAtmosphere({ dialogOpen: true });
        }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1050);
      });
    });

    const closeDialog = () => {
      window.clearTimeout(revealTimer);
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      if (lastDoor) lastDoor.setAttribute('aria-expanded', 'false');
      if (refreshAtmosphere) refreshAtmosphere({ dialogOpen: false });
      if (lastDoor) lastDoor.focus({ preventScroll: true });
    };
    add.addEventListener('click', async () => {
      const day = Number(lastDoor?.dataset.day);
      const reward = settings.days[String(day)];
      if (add.hidden || adding || dateState(day) !== 'current' || !reward?.available || !(reward.variant > 0)) { refreshDates(); return; }
      adding = true;
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      cartStatus.textContent = '';
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      try {
        const sections = cart?.getSectionsToRender?.().map(section => section.id) || ['cart-icon-bubble'];
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: reward.variant, quantity: 1 }], sections, sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('cart-add-failed');
        cartStatus.textContent = settings.added;
        // Leave an explicit cart destination even if a theme drawer cannot render.
        discover.href = `${window.Shopify?.routes?.root || '/'}cart`;
        discover.querySelector('span').textContent = settings.cart;
        if (cart?.renderContents && sections.every(id => result.sections?.[id])) {
          try {
            closeDialog();
            cart.renderContents({ ...result, id: result.items?.[0]?.product_id || result.id });
          } catch (_) {
            // The add succeeded: never invite a duplicate add because the drawer failed.
            location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
          }
        }
      } catch (_) {
        cartStatus.textContent = settings.error;
      } finally {
        adding = false;
        add.disabled = false;
        add.removeAttribute('aria-busy');
        refreshDates();
      }
    });
    close.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', () => {
      if (lastDoor) lastDoor.setAttribute('aria-expanded', 'false');
      if (refreshAtmosphere) refreshAtmosphere({ dialogOpen: false });
      if (lastDoor) lastDoor.focus({ preventScroll: true });
    });
    const clearRevealOnUnload = (event) => {
      if (event.target === root || event.target.contains(root)) {
        window.clearTimeout(revealTimer);
        window.clearInterval(dateTimer);
        document.removeEventListener('visibilitychange', refreshOnVisibility);
        document.removeEventListener('shopify:section:unload', clearRevealOnUnload);
      }
    };
    document.addEventListener('shopify:section:unload', clearRevealOnUnload);
  };

  document.querySelectorAll('[data-pa-advent]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-pa-advent]').forEach(init);
  });
})();
